import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Avatar } from '@klaro7/ui';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { useFirstChargePromo } from '@/features/wallet/hooks/useWallet';
import { reportClientErrorOnce } from '@/lib/clientErrorLog';
import { useCall } from '../context/CallContext';
import { useCountdown } from '../hooks/useCountdown';
import { ClientRunwayBadge, ClientRunwayBanner } from './ClientRunway';
import { CreditCountdown } from './CreditCountdown';
import { CallerTypeBadge } from './CallerTypeBadge';
import { PausePromoPitch } from './PausePromoPitch';
import { playRemoteMedia } from '../lib/remotePlayback';
import { attachRemoteAudioStream } from '../lib/remoteAudio';

// "12 marzo 1985 (40 anni)" — birth date + age, shown to the professional
// under the caller's name during a call.
function formatDateOfBirth(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const formatted = date.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
  const now = new Date();
  let age = now.getFullYear() - date.getFullYear();
  const m = now.getMonth() - date.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < date.getDate())) age--;
  return `${formatted} (${age} anni)`;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

function PauseOverlay({ deadline, onResume }: { deadline: string; onResume: () => void }) {
  const { user } = useAuth();
  const { billingTick } = useCall();
  const promo = useFirstChargePromo();
  const remaining = useCountdown(deadline);

  const isProfessional = user?.role === 'professional';

  return (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm p-6 text-center">
      <div className="rounded-2xl bg-white p-6 shadow-2xl max-w-sm w-full">
        <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
          <svg className="h-7 w-7 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-bold text-neutral-900">
          {isProfessional ? 'In attesa di ricarica' : 'Credito esaurito'}
        </h3>
        {!isProfessional && promo.isActive ? (
          <PausePromoPitch
            promoCreditsRemaining={promo.promoCreditsRemaining}
            currentCost={billingTick?.currentCost ?? 0}
          />
        ) : (
          <p className="mt-1 text-sm text-neutral-600">
            {isProfessional
              ? 'Il cliente sta provando a ricaricare per continuare la chiamata.'
              : 'Ricarica entro il tempo indicato per riprendere la chiamata.'}
          </p>
        )}
        <p className="mt-4 text-3xl font-bold text-amber-600 tabular-nums">{remaining}s</p>
        {!isProfessional && (
          <>
            {/* target="_blank": vedi CreditCountdown.tsx — CallScreen resta in
                vista sopra qualunque route, una navigazione in-tab a
                /my/wallet è coperta e intoccabile dallo stesso overlay. */}
            <Link
              to="/my/wallet"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 block w-full rounded-lg bg-brand-orange py-2.5 text-sm font-semibold text-white hover:bg-brand-orange/90"
            >
              Ricarica ora
            </Link>
            <button
              onClick={onResume}
              className="mt-2 w-full rounded-lg border border-neutral-200 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
            >
              Ho ricaricato, riprendi
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Pausa MANUALE della consulente (Approccio A, riscontro PEC 2026-07 punto
 * 4): copy e azioni completamente diverse dalla pausa credito — nessun pitch
 * ricarica, la fatturazione è già sospesa. Max 5 minuti (MANUAL_PAUSE_MAX_MS
 * lato server); allo scadere la sessione termina con endReason
 * 'manual_pause_timeout'.
 */
export function ManualPauseOverlay({
  deadline,
  peerName,
  isProfessional,
  onResume,
  onEndSession,
}: {
  deadline: string;
  peerName: string;
  isProfessional: boolean;
  onResume: () => void;
  onEndSession: () => void;
}) {
  const remaining = useCountdown(deadline);

  return (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm p-6 text-center">
      <div className="rounded-2xl bg-white p-6 shadow-2xl max-w-sm w-full">
        <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-brand-teal-50">
          <svg className="h-7 w-7 text-brand-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25v13.5m-7.5-13.5v13.5" />
          </svg>
        </div>
        <h3 className="text-lg font-bold text-neutral-900">
          {isProfessional ? 'Sessione in pausa' : `${peerName} ha messo in pausa la sessione`}
        </h3>
        <p className="mt-1 text-sm text-neutral-600">
          {isProfessional
            ? 'La fatturazione è sospesa. Riprendi entro 5 minuti o la sessione terminerà automaticamente.'
            : 'La fatturazione è sospesa: non stai pagando questi minuti.'}
        </p>
        <p className="mt-4 text-3xl font-bold text-brand-teal tabular-nums">{formatTime(remaining)}</p>
        {isProfessional ? (
          <button
            onClick={onResume}
            className="mt-4 w-full rounded-lg bg-brand-orange py-2.5 text-sm font-semibold text-white hover:bg-brand-orange/90"
          >
            Riprendi
          </button>
        ) : (
          <button
            onClick={onEndSession}
            className="mt-4 w-full rounded-lg border border-neutral-200 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
          >
            Termina sessione
          </button>
        )}
      </div>
    </div>
  );
}

export function CallScreen() {
  const {
    callState,
    callInfo,
    localStream,
    remoteStream,
    isAudioMuted,
    micSuspended,
    remoteMicStatus,
    isVideoEnabled,
    elapsed,
    billingTick,
    clientRunway,
    pauseInfo,
    pauseSession,
    endCall,
    cancelCall,
    toggleAudio,
    toggleVideo,
    resumeCall,
    promoEnding,
    peerDisconnected,
  } = useCall();

  const { user } = useAuth();
  const isProfessional = user?.role === 'professional';

  const localVideoRef = useRef<HTMLVideoElement | null>(null);

  // Remote AUDIO always goes through the singleton element blessed during the
  // Chiama/Rispondi tap (see lib/remoteAudio.ts). The banner is the last
  // resort if playback is still refused despite the pre-flight unlock.
  const [audioBlocked, setAudioBlocked] = useState(false);
  const callSessionId = callInfo?.callSessionId;
  const playRemoteAudio = useCallback(
    (stream: MediaStream) => {
      void attachRemoteAudioStream(stream).then((result) => {
        if (result === 'blocked') {
          setAudioBlocked(true);
          reportClientErrorOnce(`autoplay-${callSessionId ?? 'unknown'}`, {
            code: 'AUTOPLAY_BLOCKED',
            message: "Autoplay dell'audio remoto bloccato durante una chiamata",
            context: { callSessionId: callSessionId ?? null },
          });
        } else if (result === 'playing') {
          setAudioBlocked(false);
        }
      });
    },
    [callSessionId]
  );
  const unlockAudio = useCallback(() => {
    if (remoteStream) playRemoteAudio(remoteStream);
  }, [remoteStream, playRemoteAudio]);

  // Use callback ref for remote video — sets srcObject immediately when element
  // mounts. The element is muted (muted autoplay is always allowed); its audio
  // comes from the singleton above.
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);
  const setRemoteVideoRef = useCallback(
    (el: HTMLVideoElement | null) => {
      remoteVideoRef.current = el;
      if (el && remoteStream) {
        el.srcObject = remoteStream;
        void playRemoteMedia(el);
      }
    },
    [remoteStream]
  );

  // Same for local video
  const setLocalVideoRef = useCallback(
    (el: HTMLVideoElement | null) => {
      localVideoRef.current = el;
      if (el && localStream) {
        el.srcObject = localStream;
      }
    },
    [localStream]
  );

  // Update srcObject when streams change (for already-mounted elements)
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  useEffect(() => {
    if (remoteStream) {
      playRemoteAudio(remoteStream);
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = remoteStream;
        void playRemoteMedia(remoteVideoRef.current);
      }
    }
  }, [remoteStream, playRemoteAudio]);

  const isRinging = callState === 'ringing' || callState === 'initiating';
  const isConnecting = callState === 'connecting';
  const isActive = callState === 'active';
  const isPaused = callState === 'paused';
  const isEnding = callState === 'ending';
  const isVisible = isRinging || isConnecting || isActive || isPaused || isEnding;

  // Ghost/panic-tap guard: the overlay appears right under the finger that
  // tapped Chiama/Chat, and the only control is the red hangup button — on
  // iPhone this produced calls cancelled ~1s after initiate (the professional
  // then gets an SMS for a call that no longer exists). Ignore the hangup
  // button for the first moments after the overlay becomes visible.
  const visibleSinceRef = useRef(0);
  useEffect(() => {
    if (isVisible) visibleSinceRef.current = Date.now();
  }, [isVisible]);
  const handleHangup = useCallback(() => {
    if (!isActive && !isEnding && Date.now() - visibleSinceRef.current < 1500) return;
    if (isActive || isEnding) endCall();
    else cancelCall();
  }, [isActive, isEnding, endCall, cancelCall]);

  if (!isVisible) return null;

  const isVideoCall = callInfo?.callType === 'video';
  const isChat = callInfo?.callType === 'chat';

  return (
    <div className="fixed inset-0 z-[90] flex flex-col bg-gradient-to-b from-brand-navy to-[#0c0b24]">
      {isPaused && pauseInfo && (
        pauseInfo.reason === 'manual' ? (
          <ManualPauseOverlay
            deadline={pauseInfo.graceDeadline}
            peerName={callInfo?.remoteName || 'Il consulente'}
            isProfessional={isProfessional}
            onResume={resumeCall}
            onEndSession={endCall}
          />
        ) : (
          <PauseOverlay
            deadline={pauseInfo.graceDeadline}
            onResume={resumeCall}
          />
        )
      )}

      {/* Peer disconnesso: la sessione resta viva lato server in attesa che
          rientri, il billing continua — segnaliamolo senza interrompere la UI.
          Include connecting: è la finestra di rinegoziazione post-rejoin. */}
      {peerDisconnected && (isActive || isPaused || isConnecting) && (
        <div className="absolute left-1/2 top-4 z-20 -translate-x-1/2 rounded-full bg-amber-500/90 px-4 py-1.5 text-xs font-medium text-white shadow-lg">
          L'altro partecipante si è disconnesso, attendi la riconnessione…
        </div>
      )}

      {/* Autoplay blocked (WebView/in-app browser): needs a user gesture */}
      {audioBlocked && (isActive || isPaused) && (
        <button
          type="button"
          onClick={unlockAudio}
          className="absolute left-1/2 top-16 z-20 flex -translate-x-1/2 items-center gap-2 rounded-full bg-brand-orange px-5 py-3 text-sm font-semibold text-white shadow-lg animate-pulse"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" />
          </svg>
          Tocca per attivare l'audio
        </button>
      )}

      {/* Remote video / audio display. min-h-0 lets this flex child shrink so a
          tall (portrait) remote stream can't push the controls off-screen. */}
      <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden">
        {isActive && isVideoCall && remoteStream ? (
          <video
            ref={setRemoteVideoRef}
            autoPlay
            playsInline
            muted
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex flex-col items-center gap-4">
            <Avatar fallback={callInfo?.remoteName || '?'} size="lg" />
            <div className="flex flex-col items-center gap-1">
              <h2 className="text-2xl font-bold text-white">{callInfo?.remoteName}</h2>
              {isProfessional && callInfo?.callerType && (
                <CallerTypeBadge callerType={callInfo.callerType} variant="dark" />
              )}
              {callInfo?.remoteDateOfBirth && (
                <p className="text-sm text-white/70">{formatDateOfBirth(callInfo.remoteDateOfBirth)}</p>
              )}
            </div>
            <p className="text-brand-cyan">
              {isRinging && (isChat ? 'Richiesta chat inviata' : 'Chiamata in corso...')}
              {isConnecting && 'Connessione...'}
              {isActive && (isVideoCall ? 'Videochiamata' : 'Chiamata audio')}
              {isEnding && 'Chiusura in corso...'}
            </p>
            {isChat && isRinging && (
              <p className="max-w-xs text-sm text-white/60">
                In attesa che il consulente accetti. La chat si aprirà qui appena risponde
                — non chiudere la pagina.
              </p>
            )}
          </div>
        )}

        {/* Local video PIP */}
        {isActive && isVideoCall && localStream && (
          <div className="absolute bottom-24 right-4 h-40 w-28 overflow-hidden rounded-xl border-2 border-white/20 shadow-lg">
            <video
              ref={setLocalVideoRef}
              autoPlay
              playsInline
              muted
              className="h-full w-full object-cover"
              style={{ transform: 'scaleX(-1)' }}
            />
          </div>
        )}
      </div>

      {/* Status bar */}
      <div className="flex items-center justify-center gap-6 py-3 text-white/80">
        {isActive && (
          <>
            <span className="flex items-center gap-1.5 text-sm font-mono">
              <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
              {formatTime(elapsed)}
            </span>
            {/* Cliente (chi paga): costo corrente + countdown credito sempre in
                vista, che lampeggia con CTA "Ricarica" a ≤3 min. billing-tick è
                emesso solo al cliente, mai al pro. */}
            {billingTick && (
              <>
                <span className="text-sm">{billingTick.currentCost.toFixed(2)} €</span>
                <CreditCountdown remainingMinutes={billingTick.remainingMinutes} />
              </>
            )}
            {isProfessional && clientRunway && (
              <ClientRunwayBadge remainingMinutes={clientRunway.remainingMinutes} />
            )}
          </>
        )}
      </div>

      {/* Mic locale muto dal bottone: l'altro lato sente solo silenzio. Il
          banner è tappabile per riattivare al volo (col telefono all'orecchio
          il mute parte anche per sbaglio, con la guancia). */}
      {isActive && isAudioMuted && (
        <button
          type="button"
          onClick={toggleAudio}
          className="mx-auto mb-2 block max-w-md animate-pulse rounded-lg bg-red-600/95 px-4 py-2.5 text-center text-sm font-semibold text-white shadow-lg"
        >
          🎤 Microfono disattivato: {callInfo?.remoteName} non ti sente.
          Tocca qui per riattivarlo.
        </button>
      )}

      {/* Cattura mic sospesa dal sistema (schermo spento / risparmio
          energetico / telefonata GSM in corso che tiene il microfono): stesso
          silenzio lato peer, ma non è un mute volontario. */}
      {isActive && !isAudioMuted && micSuspended && (
        <div className="mx-auto mb-2 max-w-md animate-pulse rounded-lg bg-red-600/95 px-4 py-2.5 text-center text-sm font-semibold text-white shadow-lg">
          Il telefono ha sospeso il microfono: {callInfo?.remoteName} non ti
          sente. Chiudi eventuali telefonate in corso e tieni lo schermo acceso
          con la pagina in primo piano.
        </div>
      )}

      {/* L'altro lato ha il mic muto/sospeso: spiega il silenzio che senti. */}
      {isActive && remoteMicStatus && (
        <div className="mx-auto mb-2 max-w-md rounded-lg bg-amber-500/90 px-4 py-2.5 text-center text-sm font-semibold text-white shadow-lg">
          {remoteMicStatus === 'muted'
            ? `${callInfo?.remoteName} ha disattivato il microfono.`
            : `Il telefono di ${callInfo?.remoteName} ha sospeso il microfono: se non senti nulla è per questo.`}
        </div>
      )}

      {promoEnding && (
        <div className="mx-auto mb-2 max-w-md animate-pulse rounded-lg bg-amber-500/90 px-3 py-2 text-center text-sm font-semibold text-white">
          ⚠️ Tariffa agevolata 0,99 €/min in esaurimento (~{promoEnding.promoMinutesLeft} min).
          Dopo verrà applicata la tariffa del consulente.
        </div>
      )}

      {isProfessional && clientRunway && (
        <ClientRunwayBanner remainingMinutes={clientRunway.remainingMinutes} />
      )}

      {/* Controls */}
      <div className="flex items-center justify-center pb-10 pt-4">
        <div className="flex items-center gap-5">
          {isActive && (
            <>
              {/* Toggle audio */}
              <button
                onClick={toggleAudio}
                className={`flex h-14 w-14 items-center justify-center rounded-full border transition-all duration-200 hover:scale-105 active:scale-95 ${
                  isAudioMuted
                    ? 'border-white/20 bg-white/85 text-brand-navy shadow-[0_2px_8px_rgba(0,0,0,0.2)]'
                    : 'border-white/[0.08] bg-white/[0.12] text-white/90 shadow-[0_2px_8px_rgba(0,0,0,0.3)] backdrop-blur-sm hover:bg-white/20'
                }`}
              >
                {isAudioMuted ? (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
                    <path strokeLinecap="round" strokeWidth={2.5} d="M3 3l18 18" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
                  </svg>
                )}
              </button>

              {/* Toggle video */}
              {isVideoCall && (
                <button
                  onClick={toggleVideo}
                  className={`flex h-14 w-14 items-center justify-center rounded-full border transition-all duration-200 hover:scale-105 active:scale-95 ${
                    !isVideoEnabled
                      ? 'border-white/20 bg-white/85 text-brand-navy shadow-[0_2px_8px_rgba(0,0,0,0.2)]'
                      : 'border-white/[0.08] bg-white/[0.12] text-white/90 shadow-[0_2px_8px_rgba(0,0,0,0.3)] backdrop-blur-sm hover:bg-white/20'
                  }`}
                >
                  {!isVideoEnabled ? (
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
                      <path strokeLinecap="round" strokeWidth={2.5} d="M3 3l18 18" />
                    </svg>
                  ) : (
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
                    </svg>
                  )}
                </button>
              )}

              {/* Pausa manuale: solo consulente, sospende la fatturazione fino a
                  5 minuti (poi la sessione termina da sola). */}
              {isProfessional && (
                <div className="flex flex-col items-center gap-1">
                  <button
                    onClick={pauseSession}
                    className="flex h-14 w-14 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.12] text-white/90 shadow-[0_2px_8px_rgba(0,0,0,0.3)] backdrop-blur-sm transition-all duration-200 hover:scale-105 hover:bg-white/20 active:scale-95"
                    aria-label="Pausa"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25v13.5m-7.5-13.5v13.5" />
                    </svg>
                  </button>
                  <span className="text-[10px] text-white/70">Pausa</span>
                </div>
              )}
            </>
          )}

          {/* Spacing between utility and end call */}
          {isActive && <div className="w-3" />}

          {/* Hang up / Cancel. A chat request is not a phone call: no giant red
              hangup — a discreet text button avoids the reflex "abort the call"
              tap that killed chat requests within a second on mobile. */}
          {isChat && !isActive && !isEnding ? (
            <button
              onClick={handleHangup}
              className="rounded-full border border-white/20 px-6 py-2.5 text-sm font-medium text-white/80 transition-colors hover:bg-white/10"
            >
              Annulla richiesta
            </button>
          ) : (
            <button
              onClick={handleHangup}
              className="flex h-[72px] w-[72px] items-center justify-center rounded-full border border-white/10 bg-gradient-to-b from-red-500 to-red-600 text-white transition-all duration-200 hover:scale-[1.08] hover:from-red-400 hover:to-red-500 active:scale-[0.92] active:from-red-600 active:to-red-700"
              style={{
                boxShadow: '0 4px 14px rgba(220, 38, 38, 0.5), 0 2px 4px rgba(0, 0, 0, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.15)',
              }}
            >
              <svg className="h-7 w-7 rotate-[135deg]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
